import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useDeleteCardMutation, useGetDueQuery } from '../../Redux/Apis/leitnerApi'
import Icon from '../../Components/Icon'
import Dictionary from '../../Components/Dictionary'
import { useSelector } from 'react-redux'
import type { RootState } from '../../Redux/Store/Store'

export default function LeitnerReview() {
  const navigate = useNavigate()
  const params = useParams()
  const [sp] = useSearchParams()
  const boxParam = params.box || sp.get('box') || undefined
  const box = boxParam ? Number(boxParam) : undefined
  const token = useSelector((s: RootState) => s.auth.token)
  const { data, refetch, isFetching } = useGetDueQuery({ box })
  const [allCards, setAllCards] = useState<any[]>([])
  const [showDic, setShowDic] = useState(false)
  const [dicContent, setDicContent] = useState('')
  const [deleteCard] = useDeleteCardMutation()

  // Load all cards of the box (for non-due list)
  useEffect(() => {
    let aborted = false
    const load = async () => {
      if (!box) return
      let cursor: string | undefined = undefined
      const items: any[] = []
      for (let i = 0; i < 20; i++) {
        const qs = new URLSearchParams()
        qs.set('box', String(box))
        qs.set('limit', '100')
        if (cursor) qs.set('cursor', cursor)
        const res = await fetch(`/api/cards?${qs.toString()}`, { headers: token ? { Authorization: `Bearer ${token}` } : undefined })
        const json = await res.json()
        items.push(...(json?.items ?? []))
        cursor = json?.nextCursor ?? null
        if (!cursor) break
      }
      if (!aborted) setAllCards(items)
    }
    load()
    return () => { aborted = true }
  }, [box, token])

  const dueItems = (data?.items ?? []) as any[]
  const dueIds = new Set(dueItems.map((c: any) => c.id))
  const otherItems = (allCards ?? []).filter((c: any) => !dueIds.has(c.id) && !c.is_learned)

  const remove = async (id: string) => {
    if (!window.confirm('حذف این لغت؟')) return
    await deleteCard({ id }).unwrap()
    refetch()
    // reload all
    const evt = new Event('reload-box')
    window.dispatchEvent(evt)
  }
  useEffect(() => {
    const handler = () => {
      // naive reload
      (async () => {
        let cursor: string | undefined = undefined
        const items: any[] = []
        for (let i = 0; i < 20; i++) {
          const qs = new URLSearchParams()
          qs.set('box', String(box))
          qs.set('limit', '100')
          if (cursor) qs.set('cursor', cursor)
          const res = await fetch(`/api/cards?${qs.toString()}`, { headers: token ? { Authorization: `Bearer ${token}` } : undefined })
          const json = await res.json()
          items.push(...(json?.items ?? []))
          cursor = json?.nextCursor ?? null
          if (!cursor) break
        }
        setAllCards(items)
      })()
    }
    window.addEventListener('reload-box', handler)
    return () => window.removeEventListener('reload-box', handler)
  }, [box, token])

  return (
    <main className="col-lg-9">
      <div className="panel-surface p-3 mb-3 d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-light btn-sm d-flex align-items-center gap-1 shadow-sm" onClick={() => navigate('/panel/leitner')}>
            <Icon name="chevron-right" size={14}/>
            بازگشت
          </button>
          <h6 className="fw-bold m-0">مرور فلش‌کارت</h6>
        </div>
        <div className="small">{isFetching ? '...' : `مرور امروز: ${data?.dueCount ?? 0}`}</div>
      </div>

      <div className="panel-surface p-3">
        <div className="mb-2"><strong>مرور امروز</strong></div>
        <div className="d-flex flex-wrap gap-1 mb-3">
          {dueItems.length === 0 ? (
            <span className="small panel-muted">موردی ندارد</span>
          ) : dueItems.map((c:any) => (
            <WordChip key={c.id} word={c.front} onDelete={() => remove(c.id)} onDict={() => { setDicContent(c.front); setShowDic(true) }} />
          ))}
        </div>
        <div className="mb-2 mt-2"><strong>سایر لغات</strong></div>
        <div className="d-flex flex-wrap gap-1">
          {otherItems.length === 0 ? (
            <span className="small panel-muted">خالی</span>
          ) : otherItems.map((c:any) => (
            <WordChip key={c.id} word={c.front} disabled onDelete={() => remove(c.id)} onDict={() => { setDicContent(c.front); setShowDic(true) }} />
          ))}
        </div>
      </div>

      {showDic && <Dictionary content={dicContent} onClose={() => setShowDic(false)} />}
    </main>
  )
}

function WordChip({ word, onDelete, onDict, disabled }: { word: string; onDelete: () => void; onDict: () => void; disabled?: boolean }) {
  return (
    <div className={`d-inline-flex align-items-center gap-1 border rounded-pill px-2 py-1 ${disabled ? 'opacity-75' : ''}`}>
      <span className={`small ${disabled ? 'text-muted' : 'fw-bold'}`}>{word}</span>
      <button className="btn btn-link btn-sm text-decoration-none" onClick={onDict} title="دیکشنری">؟</button>
      <button className="btn btn-link btn-sm text-danger text-decoration-none" onClick={onDelete} title="حذف">×</button>
    </div>
  )
}
